import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSuperAdminClass, SAClassDrill } from '../../services/api';
import SuperAdminLayout from './SuperAdminLayout';

const PILL: Record<string, string> = { got: 'good', almost: 'warn', needs: 'crit', none: 'neutral' };
const LABEL: Record<string, string> = { got: 'Got it', almost: 'Almost', needs: 'Needs practice', none: 'No data' };
const CAT: Record<string, string> = { bright: 'Bright', hardworking: 'Hardworking', disengaged: 'Disengaged', unknown: '—' };

/** Task 147 — per-student class drill (artifact styling). */
const SuperAdminClass: React.FC = () => {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const school = sp.get('school') || '';
  const grade = Number(sp.get('grade'));
  const section = sp.get('section');
  const [data, setData] = useState<SAClassDrill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    if (!token) {
      navigate('/super-admin/login', { replace: true });
      return;
    }
    getSuperAdminClass(token, school, grade, section)
      .then(setData)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        if (/token|unauth/i.test(msg)) {
          localStorage.removeItem('superAdminToken');
          navigate('/super-admin/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate, school, grade, section]);

  return (
    <SuperAdminLayout>
      <div className="content">
        <div className="back" onClick={() => navigate(`/super-admin/schools/${encodeURIComponent(school)}`)}>‹ {school}</div>
        {loading ? (
          <p className="hint">Loading…</p>
        ) : error || !data ? (
          <p className="hint" style={{ color: 'var(--crit)' }}>{error || 'Not found.'}</p>
        ) : (
          <>
            <h1 className="pg">{data.label} · {data.school}</h1>
            <p className="pg-sub">
              {data.size} students · understanding is AI-assessed from their coach chats, not a test score.
            </p>
            <div className="tblwrap">
              <table>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Understanding <span className="ai">AI</span></th>
                    <th>Category</th>
                    <th className="num">Struggling</th>
                    <th className="num">Curiosity</th>
                  </tr>
                </thead>
                <tbody>
                  {data.students.map((st) => (
                    <tr key={st.student_id} className="clk" onClick={() => navigate(`/super-admin/students/${st.student_id}`)}>
                      <td className="who">{st.name}{st.roll != null ? <small>roll {st.roll}</small> : null}</td>
                      <td><span className={`pill ${PILL[st.bucket] || 'neutral'}`}>{LABEL[st.bucket] || st.bucket}</span></td>
                      <td>{CAT[st.category] || st.category}</td>
                      <td className="num">{st.struggled || '—'}</td>
                      <td className="num">{st.curiosity ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminClass;
